import { useEffect, useState } from "react";
import { Trash2, Mail, Phone, Download, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { WhatsAppIcon } from "@/components/site/Icons";
import { api, API, errMsg } from "@/lib/api";

const STATUS = { new: "جديد", contacted: "اتكلمنا معاه", in_progress: "شغالين عليه", done: "خلص" };

const waNumber = (p) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("0")) d = `20${d.slice(1)}`;
  return d;
};

export const ConfirmDelete = ({ onConfirm, testId, title }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-red-300 hover:bg-red-500 hover:text-white" aria-label="مسح" data-testid={testId}>
        <Trash2 className="h-4 w-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent dir="rtl" className="border-purple-500/30 bg-[var(--km-bg2)] text-white">
      <AlertDialogHeader className="text-right sm:text-right">
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-purple-200/80">الحركة دي مش هينفع ترجع فيها.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="gap-2 sm:justify-start">
        <AlertDialogAction onClick={onConfirm} className="bg-red-500 hover:bg-red-600" data-testid={`${testId}-confirm`}>امسح</AlertDialogAction>
        <AlertDialogCancel className="border-purple-500/30 bg-transparent text-white">إلغاء</AlertDialogCancel>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const RequestCard = ({ r, i, onChange, onDelete }) => {
  const logoUrl = `${API}/files/requests/${r.id}/logo`;
  const setStatus = async (status) => {
    try { onChange((await api.patch(`/admin/requests/${r.id}`, { status })).data); toast.success("اتحدثت الحالة"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const resendWa = async () => {
    try { const d = (await api.post(`/admin/requests/${r.id}/resend-whatsapp`)).data; onChange(d); d.whatsapp_status === "sent" ? toast.success("رسالة الواتساب اتبعتت") : toast.error(d.whatsapp_status === "not_configured" ? "إشعار الواتساب لسه متظبطش" : "رسالة الواتساب ماتبعتتش"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async () => {
    try { const d = (await api.post(`/admin/requests/${r.id}/resend-email`)).data; onChange(d); d.email_status === "sent" ? toast.success("الإيميل اتبعت") : toast.error("الإيميل ماتبعتش"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <article className="rounded-3xl border border-purple-500/25 bg-[var(--km-card)] p-5" data-testid={`request-card-${i}`}>
      <div className="flex flex-col gap-5 sm:flex-row">
        <a href={logoUrl} target="_blank" rel="noopener noreferrer" className="grid h-28 w-28 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white" data-testid={`request-logo-${i}`}>
          {r.is_image && r.logo_content_type !== "image/svg+xml" ? <img src={logoUrl} alt={`لوجو ${r.brand_name}`} className="h-full w-full object-contain p-2" loading="lazy" />
            : <Download className="h-8 w-8 text-[var(--km-purple)]" />}
        </a>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-head text-xl font-bold text-white" data-testid={`request-brand-${i}`}>{r.brand_name}</h3>
              <p className="text-xs text-purple-300/60">{new Date(r.created_at).toLocaleString("ar-EG")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={r.status} onValueChange={setStatus} dir="rtl">
                <SelectTrigger className="h-9 w-36 rounded-full border-purple-500/30 bg-black/30 text-white" data-testid={`request-status-select-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="border-purple-500/30 bg-[var(--km-bg2)] text-white">
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`request-status-${k}`}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <ConfirmDelete title={`تمسح طلب ${r.brand_name}؟`} onConfirm={() => onDelete(r.id)} testId={`request-delete-btn-${i}`} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <a href={`https://wa.me/${waNumber(r.phone)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 font-bold text-white" data-testid={`request-whatsapp-${i}`}>
              <WhatsAppIcon className="h-4 w-4" /> <span dir="ltr">{r.phone}</span>
            </a>
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-white"><Phone className="h-4 w-4" /> اتصال</a>
            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-white" dir="ltr"><Mail className="h-4 w-4" /> {r.email}</a>
          </div>
          <div>
            <p className="font-head text-sm font-bold text-[var(--km-cyan)]">أهم النقاط الإعلانية</p>
            <p className="whitespace-pre-wrap break-words text-purple-100/90">{r.key_points}</p>
          </div>
          {r.notes && <div><p className="font-head text-sm font-bold text-[var(--km-cyan)]">ملاحظات</p><p className="whitespace-pre-wrap break-words text-purple-100/90">{r.notes}</p></div>}
          <div className="flex flex-wrap items-center gap-3 text-xs text-purple-300/70">
            <span>{r.logo_filename}</span>
            <span data-testid={`request-email-status-${i}`}>{r.email_status === "sent" ? "✓ الإيميل اتبعت" : `الإيميل: ${r.email_status}`}</span>
            {r.email_status !== "sent" && <button onClick={resend} className="inline-flex items-center gap-1 text-[var(--km-pink)] underline" data-testid={`request-resend-email-${i}`}><RefreshCw className="h-3 w-3" /> ابعت تاني</button>}
            <span data-testid={`request-whatsapp-status-${i}`}>{r.whatsapp_status === "sent" ? "✓ الواتساب اتبعت" : r.whatsapp_status === "not_configured" ? "واتساب: مش متظبط" : `واتساب: ${r.whatsapp_status || "—"}`}</span>
            {r.whatsapp_status !== "sent" && <button onClick={resendWa} className="inline-flex items-center gap-1 text-[var(--km-pink)] underline" data-testid={`request-resend-whatsapp-${i}`}><RefreshCw className="h-3 w-3" /> ابعت واتساب تاني</button>}
          </div>
        </div>
      </div>
    </article>
  );
};

export const RequestsList = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/requests").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/admin/requests/${id}`); setItems((x) => x.filter((r) => r.id !== id)); toast.success("الطلب اتمسح"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (items === null) return <p className="text-purple-300/70" data-testid="requests-loading">بنحمّل الطلبات...</p>;
  return (
    <section data-testid="requests-list">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-display text-3xl text-white">الطلبات <span className="text-[var(--km-pink)]" data-testid="requests-count">({items.length})</span></h2>
        <button onClick={load} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-4 py-2 text-sm text-white" data-testid="requests-refresh-btn"><RefreshCw className="h-4 w-4" /> تحديث</button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-purple-500/30 p-12 text-center" data-testid="requests-empty">
          <Inbox className="mx-auto mb-3 h-12 w-12 text-[var(--km-purple)]" />
          <p className="font-head text-lg text-white">لسه مفيش طلبات</p>
          <p className="text-sm text-purple-300/70">أول ما عميل يبعت طلب من صفحة «اعمل إعلانك» هيظهر هنا.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((r, i) => <RequestCard key={r.id} r={r} i={i} onDelete={remove} onChange={(d) => setItems((x) => x.map((y) => (y.id === d.id ? d : y)))} />)}
        </div>
      )}
    </section>
  );
};
